/**
 * API Adapter implementation for mapper-ui
 *
 * Bridges the existing mapping API service to the MappingApiAdapter interface
 * required by @ute/mapper-ui-components
 */

import { mappingApi } from '../services/api/mapping';
import type {
  MappingApiAdapter,
  MappingData,
  VerificationData,
  VerificationResult,
  MappingListParams,
  MappingListResponse,
} from '@ute/mapper-ui-components';

/**
 * Creates an instance of the MappingApiAdapter using the existing mapping API
 */
export const createMappingApiAdapter = (): MappingApiAdapter => ({
  /**
   * Fetch mapping data for verification
   */
  getMappingData: async (key: string, hash: string): Promise<MappingData> => {
    const response = await mappingApi.getMappingData(key, hash);
    return response.data;
  },

  /**
   * Submit verification data
   */
  verifyMapping: async (
    key: string,
    data: VerificationData
  ): Promise<VerificationResult> => {
    const response = await mappingApi.verifyPdf(key, data);
    return response.data as VerificationResult;
  },

  /**
   * Fetch list of mappings
   */
  listMappings: async (
    params?: MappingListParams
  ): Promise<MappingListResponse> => {
    const response = await mappingApi.listPdf(params);
    return response.data;
  },

  /**
   * Delete a mapping
   */
  deleteMapping: async (key: string): Promise<void> => {
    await mappingApi.delete(key);
  },

  /**
   * Claim or release a mapping for verification.
   */
  claimMapping: async (
    key: string,
    claim: boolean
  ): Promise<{ status: string; message?: string }> => {
    const response = await mappingApi.claimMapping(key, claim);
    return response.data;
  },
});
